/**
 * PotApprovalsListView Organism
 * Pure presentation component for pot approvals list.
 * Title, period nav, and search have moved to PageHeader / PageToolbar.
 */

import { useCallback, useMemo } from 'react';
import { Box, Button, CircularProgress, Alert, Collapse } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useShallow } from 'zustand/react/shallow';
import { PotApprovalListItem } from './PotApprovalListItem';
import { StatusGroupHeader } from './StatusGroupHeader';
import { ListViewShell } from '../../../shared/components/ui/ListViewShell';
import { groupPotsByStatus } from '../utils/groupPotsByStatus';
import { usePotApprovalsUIStore } from '../stores/potApprovalsUIStore';
import type { PotSummary, PotStatus } from '../types/pot.types';

export interface PotApprovalsDataState {
  pots: PotSummary[];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
}

export interface PotApprovalsListViewProps {
  data: PotApprovalsDataState;
  onLoadMore: () => void;
  onPotClick?: (potId: string) => void;
  onMoveToActivePeriod?: (potId: string) => void;
  onMoveToPrimary?: (potId: string) => void;
  searchText: string;
}

export function PotApprovalsListView({
  data,
  onLoadMore,
  onPotClick,
  onMoveToActivePeriod,
  onMoveToPrimary,
  searchText,
}: PotApprovalsListViewProps) {
  const { pots, hasMore, isLoading, isLoadingMore, error } = data;

  const { collapsedStatuses, toggleStatusGroup } = usePotApprovalsUIStore(
    useShallow(state => ({
      collapsedStatuses: state.collapsedStatuses,
      toggleStatusGroup: state.toggleStatusGroup,
    }))
  );

  const groups = useMemo(() => groupPotsByStatus(pots), [pots]);

  const handleToggle = useCallback(
    (status: PotStatus) => () => toggleStatusGroup(status),
    [toggleStatusGroup]
  );

  if (error && !isLoading && pots.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Failed to load pots. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'auto',
      }}
    >
      <ListViewShell
        itemCount={pots.length}
        loading={isLoading}
        emptyIcon={
          <AccountBalanceWalletIcon
            sx={{ fontSize: 48, color: 'text.secondary' }}
          />
        }
        emptyTitle={
          searchText ? 'No pots match your search' : 'No pots this week'
        }
        emptyHelperText={
          searchText
            ? 'Try a different search term or clear your search to see all pots for this period.'
            : 'Pots appear here once workers book and submit work for the selected period. Use the week picker to check other periods.'
        }
        emptyMessage=""
        data-testid="pot-approvals-list"
      >
        {groups.map(group => {
          const isCollapsed = collapsedStatuses.has(group.status);
          const panelId = `pot-group-panel-${group.status}`;
          return (
            <Box key={group.status}>
              <StatusGroupHeader
                status={group.status}
                count={group.pots.length}
                expanded={!isCollapsed}
                onToggle={handleToggle(group.status)}
                panelId={panelId}
                data-testid={`status-group-${group.status}`}
              />
              <Collapse in={!isCollapsed} unmountOnExit>
                <Box id={panelId}>
                  {group.pots.map(pot => (
                    <PotApprovalListItem
                      key={pot.id}
                      pot={pot}
                      onClick={() => onPotClick?.(pot.id)}
                      onMoveToActivePeriod={onMoveToActivePeriod}
                      onMoveToPrimary={onMoveToPrimary}
                      data-testid={`pot-${pot.id}`}
                    />
                  ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </ListViewShell>

      {error && !isLoading && pots.length > 0 && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          Failed to load more pots. Please try again.
        </Alert>
      )}

      {/* Load More Button */}
      {hasMore && !isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Button
            variant="outlined"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            startIcon={isLoadingMore ? <CircularProgress size={20} /> : null}
          >
            {isLoadingMore ? 'Loading...' : 'Load More'}
          </Button>
        </Box>
      )}
    </Box>
  );
}
